// Re-synced with LC2's current apiSlice.js — the `dealsAPIKey` header was dropped
// (commit "fixing core api auth" / PR #522, security/lc-service-token-headers), and 401/
// 403 responses now get explicit handling (401 forces a hard redirect to /login). Only
// `getAccountFeed` (Resident Action Feed) and `getAccounts`/`getAccount` are consumed so
// far; the rest are kept for parity so later pages (invoices, alerts, reports) don't need
// this file touched again. A number of stake-admin/rent-match/invoicing endpoints added
// upstream since aren't ported yet since nothing in LC-new uses them.
import axios from 'axios';
import {createApi} from '@reduxjs/toolkit/query/react';

import CONSTANTS from '../../commons/constants/envConstants';
import {getCurrentToken, initializeToken} from '../../commons/utils/cookies';

initializeToken();

const axiosBaseQuery =
    () =>
    // `skipAuthRedirect` isn't a real request option — it's a local escape hatch (set by
    // e.g. searchCommunities/searchCampaigns in communitiesSlice.js) for endpoints whose
    // failures shouldn't ever force the whole app to /login. A single dropdown search
    // failing (wrong shape, transient error, endpoint not fully live yet) is not the same
    // thing as the user's session actually expiring, and forcing a full-page redirect out
    // from under someone mid-task is much worse than just showing that one widget empty.
    //
    // `rawResponse` isn't a real request option either — most endpoints' bodies are
    // `{..., data: <payload>}`, so unwrapping straight to `result.data.data` below saves
    // every transformResponse from doing it themselves. But a paginated endpoint's body is
    // `{data: [...items], pagination: {...}, status_code}` — `pagination` is a SIBLING of
    // `data`, not nested inside it, so the default unwrap silently discards it before
    // transformResponse ever runs (confirmed: this is exactly why searchCommunities/
    // searchCampaigns's `next_offset` always came back null). Endpoints that need that
    // sibling data (see communitiesSlice.js) pass `rawResponse: true` to get the whole body
    // untouched instead.
    async ({url, method, data, params, headers, skipAuthRedirect, rawResponse}) => {
        try {
            const baseUrl = CONSTANTS.BASE_API_URL;
            const token = await getCurrentToken();
            const configuredHeaders = {
                'X-Session-Token': token,
                'Content-Type': 'application/json',
                ...headers
            };

            const result = await axios({
                url: `${baseUrl}${url}`,
                method,
                data,
                params,
                headers: configuredHeaders
            });

            return {data: rawResponse ? result.data : result.data?.data ?? result.data};
        } catch (axiosError) {
            const err = axiosError;
            const status = err.response?.status;

            if (status === 401) {
                console.error('API Error 401: Unauthorized - Session token invalid or expired');
                if (typeof window !== 'undefined' && !skipAuthRedirect) {
                    window.location.href = '/login';
                }
            }

            if (status === 403) {
                console.error('API Error 403: Forbidden - User does not have permission for this resource');
            }

            return {
                error: {
                    status,
                    data: err.response?.data || err.message
                }
            };
        }
    };

export const coreApiSlice = createApi({
    reducerPath: 'coreApi',
    baseQuery: axiosBaseQuery(),
    endpoints: (builder) => ({
        getAccount: builder.query({
            query: (sfid) => ({
                url: 'accounts/',
                params: {sfid},
                method: 'get'
            }),
            providesTags: (_res, err, arg) => !err && [{type: 'Accounts', id: arg}]
        }),
        getChildAccounts: builder.query({
            query: (sfid) => ({
                url: `accounts/${sfid}/children/`,
                method: 'get'
            }),
            providesTags: [{type: 'ChildAccounts', id: 'LIST'}]
        }),
        getAccounts: builder.query({
            query: () => ({
                url: 'accounts/list/',
                method: 'get'
            }),
            providesTags: [{type: 'Accounts', id: 'LIST'}],
            transformResponse: (response) =>
                response.filter((account) => account.sfid && account.name).sort((a, b) => a.name.localeCompare(b.name))
        }),
        createAccount: builder.mutation({
            query: (data) => ({
                url: 'accounts/',
                method: 'post',
                data
            }),
            invalidatesTags: [{type: 'Accounts', id: 'LIST'}]
        }),
        getAccountAlerts: builder.query({
            query: ({sfid, startDate, endDate}) => ({
                url: `accounts/${sfid}/data_quality_alerts/`,
                method: 'get',
                params: {start_date: startDate, end_date: endDate}
            })
        }),
        getAccountFeed: builder.query({
            query: (sfid) => ({
                url: `account/${sfid}/feed`,
                method: 'get'
            })
        }),
        sendActionFeedback: builder.mutation({
            query: ({sfid, data}) => ({
                url: `account/${sfid}/send_slack_action_update`,
                method: 'post',
                data
            })
        }),
        sendOfferConfirmation: builder.mutation({
            query: ({account, resident, data}) => ({
                url: `account/${account}/feed/${resident}/confirm`,
                method: 'post',
                data
            }),
            onQueryStarted: async ({account, resident, data}, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    coreApiSlice.util.updateQueryData('getAccountFeed', account, (draft) => {
                        const feedToUpdate = draft.rows.find((d) => d.id === data?.renter_action_id && d.contact_sfid === resident);
                        if (feedToUpdate) {
                            feedToUpdate.is_confirmed = true;
                            feedToUpdate.confirmed_by = data?.confirmed_by;
                            feedToUpdate.confirmed_date = data?.confirmed_date;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch (error) {
                    patchResult.undo();
                }
            }
        }),
        getCurrentInvoicesList: builder.query({
            query: ({sfid, community_sfid}) => ({
                url: `v2/invoice/${sfid}/current/`,
                method: 'get',
                params: {community_sfid}
            })
        }),
        getPastInvoicesList: builder.query({
            query: ({sfid, community_sfid}) => ({
                url: `v2/invoice/${sfid}/past/`,
                method: 'get',
                params: {community_sfid}
            })
        }),
        sendAllTimeCashBackTransactionsReport: builder.mutation({
            query: (data) => ({
                url: `transactions_report/`,
                method: 'post',
                data
            })
        }),
        sendClaimedCashBackReport: builder.mutation({
            query: (data) => ({
                url: `claimed_report/`,
                method: 'post',
                data
            })
        }),
        sendPropertySummaryReport: builder.mutation({
            query: (data) => ({
                url: `property_summary_report/`,
                method: 'post',
                data
            })
        }),
        notifyIssueBySlack: builder.mutation({
            query: (data) => ({
                url: `notify_issue_by_slack`,
                method: 'post',
                data
            })
        })
    })
});

export const {
    useGetAccountAlertsQuery,
    useLazyGetAccountQuery,
    useLazyGetChildAccountsQuery,
    useLazyGetAccountsQuery,
    useLazyGetAccountAlertsQuery,
    useGetAccountFeedQuery,
    useSendActionFeedbackMutation,
    useSendOfferConfirmationMutation,
    useLazyGetCurrentInvoicesListQuery,
    useLazyGetPastInvoicesListQuery,
    useCreateAccountMutation,
    useSendAllTimeCashBackTransactionsReportMutation,
    useSendClaimedCashBackReportMutation,
    useSendPropertySummaryReportMutation,
    useNotifyIssueBySlackMutation
} = coreApiSlice;
